import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ShieldCheck, Smartphone, Copy, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState("form"); // form | verify | totp
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [totpSetup, setTotpSetup] = useState(null);
  const [code, setCode] = useState("");
  const [vcode, setVcode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setTotpSetup(data.totp_setup);
      await refresh();
      setStep("verify");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const verifyEmail = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/verify-email", { code: vcode });
      await refresh();
      toast.success("Email verified!");
      setStep("totp");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const resendCode = async () => {
    setErr("");
    try {
      const { data } = await api.post("/auth/resend-verification");
      toast.success(data?.message || "A new code is on its way.");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const enable = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/enable-2fa", { code });
      await refresh();
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const skipTotp = () => nav("/dashboard");

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8">
        {step === "form" ? (
          <>
            <h1 className="text-3xl font-display font-bold text-slate-900 tracking-tight">Create your account</h1>
            <p className="mt-1 text-sm text-slate-600">Get instant access to the free lab.</p>
            {err && <Alert variant="destructive" className="mt-4" data-testid="register-error"><AlertDescription>{err}</AlertDescription></Alert>}
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="register-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" data-testid="register-email-input" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" data-testid="register-password-input" type="password" required minLength={8} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" />
                <p className="text-xs text-slate-500 mt-1">Minimum 8 characters.</p>
              </div>
              <Button data-testid="register-submit-button" type="submit" className="w-full" disabled={loading}>
                {loading ? "Creating…" : "Create account"}
              </Button>
            </form>
            <div className="mt-6 text-sm text-slate-600">
              Already have an account? <Link to="/login" data-testid="register-login-link" className="text-blue-600 hover:underline">Sign in</Link>
            </div>
          </>
        ) : step === "verify" ? (
          <>
            <div className="flex items-center gap-2 mb-2">
              <MailCheck className="w-5 h-5 text-blue-600" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Step 1 of 2</span>
            </div>
            <h1 className="text-3xl font-display font-bold text-slate-900 tracking-tight">Verify your email</h1>
            <p className="mt-1 text-sm text-slate-600">We sent a 6-digit code to <span className="font-semibold">{form.email}</span>. Enter it below to unlock the labs.</p>
            {err && <Alert variant="destructive" className="mt-4" data-testid="verify-error"><AlertDescription>{err}</AlertDescription></Alert>}
            <form onSubmit={verifyEmail} className="mt-6 space-y-3">
              <div>
                <Label htmlFor="vcode">Verification code</Label>
                <Input id="vcode" data-testid="verify-code-input" inputMode="numeric" maxLength={6} required value={vcode}
                  onChange={(e) => setVcode(e.target.value.replace(/\D/g, ""))}
                  className="font-mono text-lg tracking-widest" placeholder="------" />
              </div>
              <Button data-testid="verify-submit-button" type="submit" className="w-full" disabled={loading || vcode.length < 6}>
                {loading ? "Verifying…" : "Verify email & continue"}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={resendCode} data-testid="verify-resend-button">
                Didn't get it? Resend code
              </Button>
            </form>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Step 2 of 2</span>
            </div>
            <h1 className="text-3xl font-display font-bold text-slate-900 tracking-tight">Enable 2FA</h1>
            <p className="mt-1 text-sm text-slate-600">Scan the QR code with Google Authenticator (or any TOTP app), then enter the 6-digit code below.</p>
            {err && <Alert variant="destructive" className="mt-4" data-testid="totp-error"><AlertDescription>{err}</AlertDescription></Alert>}
            {totpSetup && (
              <div className="mt-6 space-y-4">
                <div className="border border-slate-200 rounded-lg p-4 flex justify-center bg-slate-50">
                  <img src={totpSetup.qr_data_url} alt="TOTP QR" className="w-44 h-44" data-testid="totp-qr" />
                </div>
                <div>
                  <Label className="text-xs">Manual entry secret</Label>
                  <div className="flex gap-2 mt-1">
                    <Input readOnly value={totpSetup.secret} className="font-mono text-sm" data-testid="totp-secret" />
                    <Button type="button" variant="outline" size="icon"
                      onClick={() => navigator.clipboard?.writeText(totpSetup.secret)}
                      data-testid="totp-copy-button">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <form onSubmit={enable} className="space-y-3">
                  <div>
                    <Label htmlFor="otp">Enter 6-digit code</Label>
                    <Input id="otp" data-testid="totp-code-input" inputMode="numeric" maxLength={6} required value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      className="font-mono text-lg tracking-widest" />
                  </div>
                  <Button data-testid="totp-enable-button" type="submit" className="w-full" disabled={loading}>
                    <Smartphone className="w-4 h-4 mr-1" /> {loading ? "Enabling…" : "Enable 2FA & continue"}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full" onClick={skipTotp} data-testid="totp-skip-button">
                    Skip for now
                  </Button>
                </form>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
